import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { UPDATE_JOB_PROFILE } from '../../graphql/client/mutations';
import { UpdateJobProfile } from '../../types/schemaTypes';
import { isValidDate } from '../../utils';
import moment from 'moment';

interface Suggestion {
    id: string | number;
    name: string;
}

interface FieldErrors {
    wellNameError: string;
    crewError: string;
    regionError: string;
    districtError: string;
    customerError: string;
    cityError: string;
    stateError: string;
    latitudeError: string;
    longitudeError: string;
    zipError: string;
    phoneNumberError: string;
    startDateError: string;
    endDateError: string;
    epCompanyError: string;
    jdeNumberError: string;
    milesError: string;
    stagingLatitudeError: string;
    stagingLongitudeError: string;
    wellsiteGeofenceRadiusError: string;
    stagingGeofenceRadiusError: string;
    wellsiteGeofenceTypeError: string;
    stagingGeofenceTypeError: string;
}

const initialErrorsState: FieldErrors = {
    wellNameError: '',
    crewError: '',
    regionError: '',
    districtError: '',
    customerError: '',
    cityError: '',
    stateError: '',
    latitudeError: '',
    longitudeError: '',
    zipError: '',
    phoneNumberError: '',
    startDateError: '',
    endDateError: '',
    epCompanyError: '',
    jdeNumberError: '',
    milesError: '',
    stagingLatitudeError: '',
    stagingLongitudeError: '',
    wellsiteGeofenceRadiusError: '',
    stagingGeofenceRadiusError: '',
    wellsiteGeofenceTypeError: '',
    stagingGeofenceTypeError: '',
};

interface LatLng {
    lat: number;
    lng: number;
}

const useEditJobProfileForm = (jobProfileData: any, jobId: number): any => {
    const cordinatesFormatter = (cordinates) => {
        const tempCordinates: LatLng[] = []
        cordinates.map(cordinate => {
            tempCordinates.push({
                lat: cordinate.lat,
                lng: cordinate.lng,
            })
        })
        return tempCordinates;
    }

    const [inputs, setInputs] = useState({
        jobId: jobId,
        jdeNumber: jobProfileData.jobDetails.jdeNumber ? jobProfileData.jobDetails.jdeNumber : '',
        startDate: jobProfileData.jobDetails.wellStartDate
            ? new Date(jobProfileData.jobDetails.wellStartDate)
            : new Date(''),
        endDate: jobProfileData.jobDetails.wellEndDate ? new Date(jobProfileData.jobDetails.wellEndDate) : new Date(''),
        statusId: jobProfileData.jobDetails.status.statusId,
        status: jobProfileData.jobDetails.status.status,
        wellName: jobProfileData.jobDetails.jobName,
        regionId: jobProfileData.jobDetails.region.id,
        regionName: jobProfileData.jobDetails.region.regionName,
        districtId: jobProfileData.jobDetails.district.id,
        districtName: jobProfileData.jobDetails.district.districtName,
        latitude: jobProfileData.jobDetails.latitude !== null ? jobProfileData.jobDetails.latitude : 0,
        longitude: jobProfileData.jobDetails.longitude !== null ? jobProfileData.jobDetails.longitude : 0,
        directions: jobProfileData.jobDetails.directions ? jobProfileData.jobDetails.directions : '',
        addressLine1: jobProfileData.jobDetails.addressLine1 !== null ? jobProfileData.jobDetails.addressLine1 : '',
        addressLine2: jobProfileData.jobDetails.addressLine2 !== null ? jobProfileData.jobDetails.addressLine2 : '',
        city: jobProfileData.jobDetails.city !== null ? jobProfileData.jobDetails.city : '',
        stateId: jobProfileData.jobDetails.state.id,
        stateName: jobProfileData.jobDetails.state.stateName,
        zip: jobProfileData.jobDetails.zip !== null ? jobProfileData.jobDetails.zip : '',
        customerId: jobProfileData.customerDetails.customerId,
        customerName: jobProfileData.customerDetails.customerName,
        crewId: jobProfileData.jobDetails.crewId,
        crewName: jobProfileData.customerDetails.crew,
        epCompanyId: jobProfileData.jobDetails.epCompanyId ? jobProfileData.jobDetails.epCompanyId : 0,
        epCompany: jobProfileData.customerDetails.epCompany,
        contact: jobProfileData.customerDetails.contact !== null ? jobProfileData.customerDetails.contact : '',
        phoneNumber:
            jobProfileData.customerDetails.phoneNumber !== null ? jobProfileData.customerDetails.phoneNumber : '',
        accessorial: jobProfileData.jobDetails.accessorialId ? jobProfileData.jobDetails.accessorialId : 0,
        miles: jobProfileData.jobDetails.miles ? jobProfileData.jobDetails.miles : '',
        accessorialName: jobProfileData.jobDetails.accessorialName ? jobProfileData.jobDetails.accessorialName : '',
        wellsiteGeoFenceType: jobProfileData.jobDetails.wellsiteGeoFenceType
            ? jobProfileData.jobDetails.wellsiteGeoFenceType
            : 'Circle',
        wellsiteGeoFenceRadius: jobProfileData.jobDetails.wellsiteGeoFenceRadius
            ? jobProfileData.jobDetails.wellsiteGeoFenceRadius
            : 0,
        wellsitePolygonCoordinates: jobProfileData.jobDetails.wellsitePolygonCoordinates
            ? cordinatesFormatter(jobProfileData.jobDetails.wellsitePolygonCoordinates)
            : [],
        stagingLatitude:
            jobProfileData.jobDetails.stagingLatitude !== null && jobProfileData.jobDetails.stagingLatitude != 0
                ? jobProfileData.jobDetails.stagingLatitude
                : '',
        stagingLongitude:
            jobProfileData.jobDetails.stagingLongitude !== null && jobProfileData.jobDetails.stagingLongitude != 0
                ? jobProfileData.jobDetails.stagingLongitude
                : '',
        stagingGeoFenceType: jobProfileData.jobDetails.stagingGeoFenceType
            ? jobProfileData.jobDetails.stagingGeoFenceType
            : 'None',
        stagingGeoFenceRadius: jobProfileData.jobDetails.stagingGeoFenceRadius
            ? jobProfileData.jobDetails.stagingGeoFenceRadius
            : 0,
        stagingPolygonCoordinates: jobProfileData.jobDetails.stagingPolygonCoordinates
            ? cordinatesFormatter(jobProfileData.jobDetails.stagingPolygonCoordinates)
            : [],
        isBellyDump: jobProfileData.jobDetails.isBellyDump
        ? jobProfileData.jobDetails.isBellyDump
        : false,
    });
    const [fieldErrors, setFieldErrors] = useState(initialErrorsState);
    const [mutationError, setMutationError] = useState('');
    const [disable, setDisable] = useState(true);

    const [updateJob] = useMutation<UpdateJobProfile>(UPDATE_JOB_PROFILE, {
        onCompleted: (data) => {
            const updateJobProfileErrors = data.updateJobProfile?.errors ?? [];
            if (updateJobProfileErrors.length === 0) {
                window.history.back();
                return;
            }
            const updateJobErrors = updateJobProfileErrors[0];
            setMutationError(updateJobErrors ?? '');
            if (updateJobProfileErrors.find((error: string | null) => error === 'JDE_NUMBER_NUMBER_USED')) {
                setFieldErrors({
                    ...fieldErrors,
                    jdeNumberError: 'JDE # already exists for another Job.',
                });
            }
        },
        onError: (e) => {
            if (CombinedGraphQLErrors.is(e)) {
                setMutationError(e.errors[0]?.message || e.message);
                console.log('GraphQL Error', e.errors);
            } else {
                setMutationError(e.message);
                console.log('Error', e.message);
            }
        }
    });

    const validateRequiredFields = (): any => {
        let wellNameError = '';
        let crewError = '';
        let regionError = '';
        let districtError = '';
        let customerError = '';
        let cityError = '';
        let latitudeError = '';
        let longitudeError = '';
        let zipError = '';
        let stateError = '';
        let milesError = '';
        let startDateError = '';
        let endDateError = '';
        let wellsiteGeofenceRadiusError = '';
        let stagingGeofenceRadiusError = '';
        let wellsiteGeofenceTypeError = '';
        let stagingGeofenceTypeError = '';
        const requiredFieldMessage = 'This field is required.';
        const cordinatesRequiredFieldMessage = 'Please draw polygon on the map.';
        if (inputs.wellName === '') {
            wellNameError = requiredFieldMessage;
        }

        if (inputs.crewId === 0) {
            crewError = requiredFieldMessage;
        }

        if (inputs.regionId === 0) {
            regionError = requiredFieldMessage;
        }

        if (inputs.districtId === 0) {
            districtError = requiredFieldMessage;
        }

        if (inputs.customerId === 0) {
            customerError = requiredFieldMessage;
        }

        
        if (!inputs.startDate || isNaN(new Date(inputs.startDate).getDate())) {
            startDateError = requiredFieldMessage;
        }

        if (!inputs.endDate || isNaN(new Date(inputs.endDate).getDate())) {
            endDateError = requiredFieldMessage;
        }

        if (inputs.city === '') {
            cityError = requiredFieldMessage;
        }

        if (inputs.latitude == 0 || inputs.latitude.toString() === '') {
            latitudeError = 'Value should not be 0. Please enter valid coordinates.';
        }

        if (inputs.longitude == 0 || inputs.longitude.toString() === '') {
            longitudeError = 'Value should not be 0. Please enter valid coordinates.';
        }

        if (inputs.zip === '') {
            zipError = requiredFieldMessage;
        }

        if (inputs.stateId === 0) {
            stateError = requiredFieldMessage;
        }

        if (inputs.accessorial !== 0 && inputs.miles === '') {
            milesError = 'Miles are required when an accessorial is selected.';
        }

        if (inputs.wellsiteGeoFenceType === '') {
            wellsiteGeofenceTypeError = requiredFieldMessage;
        }

        if (inputs.stagingGeoFenceType === '') {
            stagingGeofenceTypeError = requiredFieldMessage;
        }

        if (inputs.wellsiteGeoFenceRadius === '') {
            wellsiteGeofenceTypeError = requiredFieldMessage;
        }

        if (inputs.wellsiteGeoFenceType == 'Circle' && (!inputs.wellsiteGeoFenceRadius || parseInt(inputs.wellsiteGeoFenceRadius.toString()) === 0)) {
            wellsiteGeofenceRadiusError = requiredFieldMessage;
        }

        if (inputs.wellsiteGeoFenceType == 'Polygon' && inputs.wellsitePolygonCoordinates.length === 0) {
            wellsiteGeofenceTypeError = cordinatesRequiredFieldMessage;
        }

        if (inputs.stagingGeoFenceType == 'Circle' && (!inputs.stagingGeoFenceRadius || parseInt(inputs.stagingGeoFenceRadius.toString()) === 0)) {
            stagingGeofenceRadiusError = requiredFieldMessage;
        }

        if (inputs.stagingGeoFenceType == 'Polygon' && inputs.stagingPolygonCoordinates.length === 0) {
            stagingGeofenceTypeError = cordinatesRequiredFieldMessage;
        }

        return {
            wellNameError,
            crewError,
            regionError,
            districtError,
            customerError,
            cityError,
            latitudeError,
            longitudeError,
            zipError,
            stateError,
            milesError,
            startDateError,
            endDateError,
            stagingGeofenceTypeError,
            stagingGeofenceRadiusError,
            wellsiteGeofenceTypeError,
            wellsiteGeofenceRadiusError,
        };
    };

    const checkForRequiredFields = (name: any, value?: any): boolean => {
        if (
            (name === 'wellName' ? '' : inputs.wellName === '') ||
            inputs.crewId === 0 ||
            inputs.regionId === 0 ||
            (name === 'regionId' ? true : '') ||
            (name === 'districtId' ? '' : inputs.districtId === 0) ||
            inputs.customerId === 0 ||
            (name === 'city' ? '' : inputs.city === '') ||
            inputs.stateId === 0 ||
            inputs.latitude === 0 ||
            inputs.longitude === 0 ||
            (name === 'startDate' ? '' : !inputs.startDate) ||
            (name === 'endDate' ? '' : !inputs.endDate) ||
            inputs.zip === '' ||
            (name === 'miles' ? '' : inputs.accessorial !== 0 && inputs.miles === '') ||
            (name === 'accessorial' && value !== 0 && inputs.miles === '' ? true : false)
        ) {
            return false;
        }
        return true;
    };

    const handleDropdownChange = (fieldName: string, newValue: string): void => {
        if (fieldName == 'stagingGeoFenceType') {
            setDisable(false);
            return;
        }
        if (fieldName === 'regionId') {
            setInputs(inputs => ({ ...inputs, [fieldName]: newValue }));
            setInputs(inputs => ({ ...inputs, districtId: 0 }));
            checkForRequiredFields(fieldName) ? setDisable(false) : setDisable(true);
        } else {
            setInputs(inputs => ({ ...inputs, [fieldName]: +newValue }));
            checkForRequiredFields(fieldName, +newValue) ? setDisable(false) : setDisable(true);
        }
    };

    const handleMapSave = () => {
        setDisable(false);
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
        if (event.target.name === 'jdeNumber') {
            setFieldErrors({
                ...fieldErrors,
                jdeNumberError: '',
            });
        }
        checkForRequiredFields(event.target.name) ? setDisable(false) : setDisable(true);
    };

    const handleInputChangeForRequiredFields = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
        if (event.target.value === null || event.target.value === '') {
            setDisable(true);
        } else {
            checkForRequiredFields(event.target.name) ? setDisable(false) : setDisable(true);
        }
    };

    const handleAutoCompleteChange = (inputName: string, value: Suggestion): void => {
        switch (inputName) {
            case 'crew': {
                setInputs({
                    ...inputs,
                    crewName: value.name,
                    crewId: parseInt(value.id.toString()),
                });
                if (
                    value.id !== 0 &&
                    inputs.wellName !== '' &&
                    inputs.regionId !== 0 &&
                    inputs.districtId !== 0 &&
                    inputs.customerId !== 0 &&
                    inputs.city !== '' &&
                    inputs.stateId !== 0
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
            case 'customerName': {
                setInputs({
                    ...inputs,
                    crewName: '',
                    crewId: 0,
                    customerName: value.name,
                    customerId: parseInt(value.id.toString()),
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        customerError: 'No customer found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        customerError: '',
                    });
                }
                if (
                    value.name !== '' &&
                    value.id !== 0 &&
                    inputs.crewId !== 0 &&
                    inputs.wellName !== '' &&
                    inputs.regionId !== 0 &&
                    inputs.districtId !== 0 &&
                    inputs.stateId !== 0 &&
                    inputs.city !== ''
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
            case 'epCompany': {
                setInputs({
                    ...inputs,
                    epCompany: value.name,
                    epCompanyId: parseInt(value.id.toString()),
                });
                if (value.name !== '' && value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        epCompanyError: 'You must select one of the suggested options.',
                    });
                    setDisable(true);
                } else if (value.name === '' || (value.name !== '' && value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        epCompanyError: '',
                    });
                }
                if (
                    value.name !== '' &&
                    value.id !== 0 &&
                    inputs.wellName !== '' &&
                    inputs.crewId !== 0 &&
                    inputs.regionId !== 0 &&
                    inputs.districtId !== 0 &&
                    inputs.stateId !== 0 &&
                    inputs.city !== ''
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
            case 'stateName': {
                setInputs({
                    ...inputs,
                    stateName: value.name,
                    stateId: parseInt(value.id.toString()),
                });
                if (
                    value.name !== '' &&
                    value.id !== 0 &&
                    inputs.wellName !== '' &&
                    inputs.crewId !== 0 &&
                    inputs.regionId !== 0 &&
                    inputs.districtId !== 0 &&
                    inputs.customerId !== 0 &&
                    inputs.city !== ''
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
        }
    };
    const handleDateInputChange = (inputName: string, value: Date): void => {
        setInputs({
            ...inputs,
            [inputName]: value,
        });

        checkForRequiredFields(inputName) ? setDisable(false) : setDisable(true);
    };

    const validateZipNumber = (value: any): void => {
        let zipError = '';
        if (value && !/^\d+$/.test(value)) {
            zipError = 'This field should only contain numeric values.';
            setDisable(true);
        } else if (value && value.length > 5) {
            zipError = 'The number of characters must not exceed 5 digits.';
            setDisable(true);
        }

        setFieldErrors({
            ...fieldErrors,
            zipError,
        });
    };

    const validateLatitude = (latitudeValue: any): void => {
        let latitudeError = '';
        if (latitudeValue && (latitudeValue < -90 || latitudeValue > 90)) {
            latitudeError = 'The valid range is from -90 to 90.';
            setDisable(true);
        }

        setFieldErrors({
            ...fieldErrors,
            latitudeError,
        });
    };

    const validateStagingLongitude = (longitudeValue: any): void => {
        let stagingLongitudeError = '';
        if (longitudeValue && (longitudeValue < -180 || longitudeValue > 180)) {
            stagingLongitudeError = 'The valid range is from -180 to 180.';
        }

        if (longitudeValue == 0) {
            stagingLongitudeError = 'Value should not be 0. Please enter valid coordinates.';
        }

        setFieldErrors({
            ...fieldErrors,
            stagingLongitudeError,
        });
    };

    const validateStagingLatitude = (latitudeValue: any): void => {
        let stagingLatitudeError = '';
        if (latitudeValue && (latitudeValue < -90 || latitudeValue > 90)) {
            stagingLatitudeError = 'The valid range is from -90 to 90.';
        }

        if (latitudeValue == 0) {
            stagingLatitudeError = 'Value should not be 0. Please enter valid coordinates.';
        }

        setFieldErrors({
            ...fieldErrors,
            stagingLatitudeError,
        });
    };

    const validatePhoneNumber = (value: any): void => {
        let phoneNumberError = '';
        if (value && !/^\d+$/.test(value)) {
            phoneNumberError = 'This field should only contain numeric values.';
            setDisable(true);
        } else if (value && value.length > 10) {
            phoneNumberError = 'The number of characters must not exceed 10 digits.';
            setDisable(true);
        }

        setFieldErrors({
            ...fieldErrors,
            phoneNumberError,
        });
    };

    const validateLongitude = (longitudeValue: any): void => {
        let longitudeError = '';
        if (longitudeValue && (longitudeValue < -180 || longitudeValue > 180)) {
            longitudeError = 'The valid range is from -180 to 180.';
            setDisable(true);
        }

        setFieldErrors({
            ...fieldErrors,
            longitudeError,
        });
    };

    const validateStartDate = (value: string): void => {
        const startDate = !!value ? new Date(value) : null;
        let startDateError = '';

        if (!isValidDate(startDate)) {
            startDateError = 'Please enter a valid date.';
            setDisable(true);
        } else if (startDate && inputs.endDate && startDate > inputs.endDate) {
            startDateError = 'Well start date should not be greater than end date.';
            setDisable(true);
        } else if (checkForRequiredFields(startDate)) {
            setDisable(false);
        }

        setFieldErrors({
            ...fieldErrors,
            startDateError,
        });
    };

    const validateEndDate = (value: string): void => {
        const endDate = !!value ? new Date(value) : null;
        let endDateError = '';

        if (!isValidDate(endDate)) {
            endDateError = 'Please enter a valid date.';
            setDisable(true);
        } else if (endDate && inputs.startDate && endDate < inputs.startDate) {
            endDateError = 'Well end date should not be less than start date.';
            setDisable(true);
        } else if (checkForRequiredFields(endDate)) {
            setDisable(false);
        }

        setFieldErrors({
            ...fieldErrors,
            endDateError,
        });
    };

    const handleSubmit = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        const {
            wellNameError,
            crewError,
            regionError,
            districtError,
            customerError,
            cityError,
            latitudeError,
            longitudeError,
            startDateError,
            endDateError,
            zipError,
            stateError,
            milesError,
            stagingGeofenceRadiusError,
            stagingGeofenceTypeError,
            wellsiteGeofenceRadiusError,
            wellsiteGeofenceTypeError,
        } = validateRequiredFields();

        setFieldErrors({
            ...fieldErrors,
            wellNameError,
            crewError,
            regionError,
            districtError,
            customerError,
            cityError,
            latitudeError,
            longitudeError,
            startDateError,
            endDateError,
            zipError,
            stateError,
            milesError,
            stagingGeofenceTypeError,
            stagingGeofenceRadiusError,
            wellsiteGeofenceRadiusError,
            wellsiteGeofenceTypeError,
        });

        const {
            phoneNumberError,
            epCompanyError,
            jdeNumberError,
        } = fieldErrors;

        if (
            wellNameError === '' &&
            crewError === '' &&
            regionError === '' &&
            districtError === '' &&
            customerError === '' &&
            cityError === '' &&
            stateError === '' &&
            latitudeError === '' &&
            longitudeError === '' &&
            zipError === '' &&
            phoneNumberError === '' &&
            startDateError === '' &&
            endDateError === '' &&
            epCompanyError === '' &&
            jdeNumberError === '' &&
            stagingGeofenceRadiusError === '' &&
            wellsiteGeofenceRadiusError === '' &&
            wellsiteGeofenceTypeError === '' &&
            stagingGeofenceTypeError === ''
        ) {
            const updateJobInput = {
                jobNumber: '',
                extSystemIdentifier: inputs.jdeNumber,
                startDate: moment(inputs.startDate).utc() || '',
                endDate: moment(inputs.endDate).utc() || '',
                statusId: +inputs.statusId,
                wellName: inputs.wellName,
                regionId: +inputs.regionId,
                districtId: +inputs.districtId,
                latitude: parseFloat(inputs.latitude.toString()),
                longitude: parseFloat(inputs.longitude.toString()),
                directions: inputs.directions,
                addressLine1: inputs.addressLine1,
                addressLine2: inputs.addressLine2,
                city: inputs.city,
                stateId: inputs.stateId,
                zip: inputs.zip,
                customerId: inputs.customerId,
                crewId: inputs.crewId,
                epCompanyId: inputs.epCompanyId !== 0 ? inputs.epCompanyId : null,
                contact: inputs.contact,
                phoneNumber: inputs.phoneNumber,
                accessorialId: +inputs.accessorial,
                miles: inputs.miles,
                wellsiteGeoFenceType: inputs.wellsiteGeoFenceType,
                wellsiteGeoFenceRadius: parseInt(inputs.wellsiteGeoFenceRadius),
                wellsitePolygonCoordinates: inputs.wellsitePolygonCoordinates,
                stagingLatitude: parseFloat(inputs.stagingLatitude.toString()),
                stagingLongitude: parseFloat(inputs.stagingLongitude.toString()),
                stagingGeoFenceType: inputs.stagingGeoFenceType,
                stagingGeoFenceRadius: parseInt(inputs.stagingGeoFenceRadius),
                stagingPolygonCoordinates: inputs.stagingPolygonCoordinates,
                isBellyDump: inputs.isBellyDump,
            };

            const updateJobEdit = {
                jobId: inputs.jobId,
                jobProfileValues: updateJobInput,
            };

            updateJob({
                variables: { input: updateJobEdit },
            });
        }
    };

    return {
        handleSubmit,
        handleDropdownChange,
        handleInputChange,
        handleAutoCompleteChange,
        handleDateInputChange,
        inputs,
        fieldErrors,
        validateRequiredFields,
        validateLatitude,
        validateLongitude,
        validateStagingLatitude,
        validateStagingLongitude,
        validateZipNumber,
        validatePhoneNumber,
        validateStartDate,
        validateEndDate,
        handleMapSave,
        disable,
        handleInputChangeForRequiredFields,
        mutationError,
        setMutationError,
        setDisable,
    };
};

export default useEditJobProfileForm;
